import React, { useState, useRef, useEffect } from 'react';
import { FaUsers, FaUser, FaBell, FaBellSlash, FaPaperPlane, FaCheck, FaCheckDouble } from 'react-icons/fa';
import { MuteMenu } from './MuteMenu';

const priorityOptions = [
  { value: 'normal', label: 'Normal', icon: '💬', color: 'text-gray-600' },
  { value: 'announcement', label: 'Announcement', icon: '📢', color: 'text-orange-600', desc: 'Notifies everyone' },
  { value: 'emergency', label: 'Emergency', icon: '🚨', color: 'text-red-600', desc: 'Urgent notification' }
];

export function ChatView({ conversation, messages, onSendMessage, onMute, currentUser, socket, typingUsers, onMarkRead }) {
  const [messageInput, setMessageInput] = useState('');
  const [showMuteMenu, setShowMuteMenu] = useState(false);
  const [messagePriority, setMessagePriority] = useState('normal');
  const [showPriorityMenu, setShowPriorityMenu] = useState(false);
  const [showMentionSuggestions, setShowMentionSuggestions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    // Mark all messages as read when viewing
    if (conversation && onMarkRead) {
      onMarkRead(conversation.conversation_id);
    }
  }, [messages, conversation, onMarkRead]);

  // Parse @mentions from message input
  const parseMentions = (text) => {
    const mentionRegex = /@(\w+)/g;
    const mentions = [];
    let match;
    while ((match = mentionRegex.exec(text)) !== null) {
      // Find user by name in participants
      const participant = conversation.participants?.find(
        p => p.name.toLowerCase().includes(match[1].toLowerCase())
      );
      if (participant) {
        mentions.push(participant.user_id);
      }
    }
    return mentions;
  };

  const handleSend = (e) => {
    e.preventDefault();
    if (!messageInput.trim()) return;
    
    const mentions = parseMentions(messageInput);
    onSendMessage(messageInput, messagePriority, mentions);
    setMessageInput('');
    setMessagePriority('normal');
    
    // Stop typing indicator
    if (socket && conversation) {
      socket.emit('typing_stop', {
        conversation_id: conversation.conversation_id,
        user_id: currentUser?.user_id,
        user_name: currentUser?.name
      });
      setIsTyping(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setMessageInput(value);
    
    // Typing indicator
    if (socket && conversation && currentUser) {
      if (!isTyping && value.length > 0) {
        setIsTyping(true);
        socket.emit('typing_start', {
          conversation_id: conversation.conversation_id,
          user_id: currentUser.user_id,
          user_name: currentUser.name
        });
      }
      
      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Set new timeout to stop typing after 2 seconds of inactivity
      typingTimeoutRef.current = setTimeout(() => {
        if (isTyping) {
          setIsTyping(false);
          socket.emit('typing_stop', {
            conversation_id: conversation.conversation_id,
            user_id: currentUser.user_id,
            user_name: currentUser.name
          });
        }
      }, 2000);
    }
    
    // Check for @ at the end for mention suggestions
    const lastAt = value.lastIndexOf('@');
    if (lastAt !== -1) {
      const afterAt = value.substring(lastAt + 1);
      if (!afterAt.includes(' ')) {
        setMentionFilter(afterAt.toLowerCase());
        setShowMentionSuggestions(true);
      } else {
        setShowMentionSuggestions(false);
      }
    } else {
      setShowMentionSuggestions(false);
    }
  };

  const insertMention = (participant) => {
    const lastAt = messageInput.lastIndexOf('@');
    const beforeAt = messageInput.substring(0, lastAt);
    const name = participant.name.replace(/\s+/g, '');
    setMessageInput(beforeAt + '@' + name + ' ');
    setShowMentionSuggestions(false);
  };

  if (!conversation) {
    return (
      <div className="h-full flex items-center justify-center text-gray-500">
        <div className="text-center">
          <FaUsers className="text-6xl mx-auto mb-4 text-gray-300" />
          <p className="text-lg">Select a conversation</p>
          <p className="text-sm mt-1">Or start a new message</p>
        </div>
      </div>
    );
  }

  const filteredParticipants = conversation.participants?.filter(
    p => p.user_id !== currentUser?.user_id && 
         p.name.toLowerCase().includes(mentionFilter)
  ) || [];

  return (
    <div className="h-full flex flex-col">
      {/* Chat Header */}
      <div className="p-4 border-b border-gray-200 bg-white flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            conversation.type === 'family' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'
          }`}>
            {conversation.type === 'family' ? <FaUsers /> : <FaUser />}
          </div>
          <div>
            <h3 className="font-semibold text-gray-800">{conversation.display_name}</h3>
            <p className="text-xs text-gray-500">
              {conversation.type === 'family' 
                ? `${conversation.participants?.length || 0} members` 
                : 'Direct Message'}
            </p>
          </div>
        </div>
        
        <div className="relative">
          <button
            onClick={() => setShowMuteMenu(!showMuteMenu)}
            className="p-2 text-gray-500 hover:bg-gray-100 rounded-full"
            data-testid="mute-toggle-btn"
          >
            {conversation.is_muted ? <FaBellSlash /> : <FaBell />}
          </button>
          
          <MuteMenu
            isOpen={showMuteMenu}
            onClose={() => setShowMuteMenu(false)}
            onMute={onMute}
            currentMuteStatus={conversation.is_muted}
          />
        </div>
      </div>
      
      {/* Muted Banner */}
      {conversation.is_muted && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2 flex items-center justify-between">
          <div className="flex items-center space-x-2 text-yellow-700 text-sm">
            <FaBellSlash />
            <span>Notifications paused</span>
            {conversation.mute_until && (
              <span className="text-yellow-600">
                until {new Date(conversation.mute_until).toLocaleString()}
              </span>
            )}
          </div>
          <button
            onClick={() => onMute(false, null)}
            className="text-xs text-yellow-700 hover:text-yellow-800 underline"
          >
            Resume
          </button>
        </div>
      )}
      
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
        {messages.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <p>No messages yet</p>
            <p className="text-sm">Start the conversation!</p>
          </div>
        ) : (
          messages.map((msg) => {
            const isOwn = msg.sender_id === currentUser?.user_id;
            return (
              <div
                key={msg.message_id}
                className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                data-testid={`message-${msg.message_id}`}
              >
                <div className={`max-w-[70%] ${isOwn ? 'order-2' : ''}`}>
                  {!isOwn && (
                    <p className="text-xs text-gray-500 mb-1 ml-1">{msg.sender_name}</p>
                  )}
                  <div className={`rounded-2xl px-4 py-2 ${
                    isOwn 
                      ? 'bg-blue-600 text-white rounded-br-sm' 
                      : 'bg-white text-gray-800 shadow-sm rounded-bl-sm'
                  } ${msg.priority === 'announcement' ? 'border-2 border-yellow-400' : ''} 
                    ${msg.priority === 'emergency' ? 'border-2 border-red-500' : ''}`}
                  >
                    {msg.priority !== 'normal' && (
                      <span className={`text-xs font-bold uppercase ${isOwn ? 'text-blue-100' : 'text-gray-500'}`}>
                        {msg.priority === 'announcement' ? '📢 ' : '🚨 '}
                      </span>
                    )}
                    {msg.content}
                  </div>
                  <div className={`flex items-center mt-1 ${isOwn ? 'justify-end mr-1' : 'ml-1'}`}>
                    <span className="text-xs text-gray-400">
                      {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {/* Read receipt indicator for own messages */}
                    {isOwn && (
                      <span className="ml-1" title={msg.read_by?.length > 0 ? `Read by ${msg.read_by.length}` : 'Sent'}>
                        {msg.read_by?.length > 0 ? (
                          <FaCheckDouble className="text-blue-400 text-xs" />
                        ) : (
                          <FaCheck className="text-gray-400 text-xs" />
                        )}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
        
        {/* Typing Indicator */}
        {typingUsers && typingUsers.length > 0 && (
          <div className="flex items-center space-x-2 text-gray-500 text-sm py-2 px-4" data-testid="typing-indicator">
            <div className="flex space-x-1">
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
            <span>
              {typingUsers.length === 1 
                ? `${typingUsers[0].user_name} is typing...`
                : `${typingUsers.length} people are typing...`
              }
            </span>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      
      {/* Message Input */}
      <form onSubmit={handleSend} className="p-4 bg-white border-t border-gray-200">
        {/* Mention Suggestions */}
        {showMentionSuggestions && filteredParticipants.length > 0 && (
          <div className="mb-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-32 overflow-y-auto">
            {filteredParticipants.map(p => (
              <button
                key={p.user_id}
                type="button"
                onClick={() => insertMention(p)}
                className="w-full px-4 py-2 text-left hover:bg-blue-50 flex items-center space-x-2"
              >
                <FaUser className="text-gray-400" />
                <span className="text-gray-800">@{p.name}</span>
              </button>
            ))}
          </div>
        )}
        
        {/* Priority Selector */}
        {conversation.type === 'family' && (
          <div className="mb-2 flex items-center space-x-2">
            <span className="text-sm text-gray-500">Priority:</span>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowPriorityMenu(!showPriorityMenu)}
                className={`px-3 py-1 rounded-full text-sm flex items-center space-x-1 border ${
                  messagePriority === 'announcement' ? 'bg-orange-100 border-orange-300 text-orange-700' :
                  messagePriority === 'emergency' ? 'bg-red-100 border-red-300 text-red-700' :
                  'bg-gray-100 border-gray-300 text-gray-700'
                }`}
              >
                <span>{priorityOptions.find(p => p.value === messagePriority)?.icon}</span>
                <span>{priorityOptions.find(p => p.value === messagePriority)?.label}</span>
              </button>
              
              {showPriorityMenu && (
                <div className="absolute bottom-full left-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-48 z-10">
                  {priorityOptions.map(option => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        setMessagePriority(option.value);
                        setShowPriorityMenu(false);
                      }}
                      className={`w-full px-4 py-2 text-left hover:bg-gray-50 ${option.color}`}
                    >
                      <span className="mr-2">{option.icon}</span>
                      <span>{option.label}</span>
                      {option.desc && <p className="text-xs text-gray-500 mt-0.5">{option.desc}</p>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        
        <div className="flex items-center space-x-3">
          <input
            type="text"
            value={messageInput}
            onChange={handleInputChange}
            placeholder={conversation.type === 'family' ? "Type a message... (use @ to mention)" : "Type a message..."}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            data-testid="message-input"
          />
          <button
            type="submit"
            disabled={!messageInput.trim()}
            className={`p-3 text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed ${
              messagePriority === 'emergency' ? 'bg-red-600 hover:bg-red-700' :
              messagePriority === 'announcement' ? 'bg-orange-600 hover:bg-orange-700' :
              'bg-blue-600 hover:bg-blue-700'
            }`}
            data-testid="send-message-btn"
          >
            <FaPaperPlane />
          </button>
        </div>
      </form>
    </div>
  );
}
